/**
 * The operator's live config (admin-dashboard §4.9-§4.10): feature flags,
 * public settings, announcement banners and maintenance, from
 * `GET /api/v1/config` at start and the gateway's `CONFIG_UPDATE` after
 * every change. The server enforces every flag itself; this only tells the
 * UI, so a feature is shown as off only when the server said so: before
 * the first answer (or when the server has no config route yet) everything
 * stays as it was.
 */
import {
  CLIENT_CONFIG_PATH,
  isNewerConfig,
  maintenancePhase,
  type ClientConfig,
  type FeatureFlagKey,
  type GatewayDispatch,
  type MaintenancePhase,
  type PublicAnnouncement,
  type RoleIconMode,
} from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { api } from '../lib/api';
import { storage } from '../lib/storage';

export type ConfigStatus = 'unknown' | 'ok' | 'error';

interface ConfigState {
  config: ClientConfig | null;
  status: ConfigStatus;
  /** Announcement ids this browser dismissed. */
  dismissed: string[];
}

const DISMISSED_KEY = 'announcements.dismissed';

export const useConfig = create<ConfigState>(() => ({
  config: null,
  status: 'unknown',
  dismissed: storage.getJson<string[]>(DISMISSED_KEY, []),
}));

type Listener = (next: ClientConfig, prev: ClientConfig | null) => void;
const listeners = new Set<Listener>();

/** Runs after each accepted config (the voice controller leaves when voice goes off). */
export function onConfigChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Takes a config from the server. Older versions are ignored (a late HTTP
 * answer must not undo a newer CONFIG_UPDATE); `force` takes the answer for
 * this user after sign-in, which can resolve flags differently at the same
 * version.
 */
export function applyConfig(incoming: ClientConfig, o: { force?: boolean } = {}): boolean {
  const prev = useConfig.getState().config;
  if (!o.force && !isNewerConfig(prev, incoming)) return false;
  if (o.force && prev && incoming.version < prev.version) return false;
  useConfig.setState({ config: incoming, status: 'ok' });
  for (const fn of listeners) fn(incoming, prev);
  return true;
}

/**
 * The gateway glue (store/session.ts): `CONFIG_UPDATE` applies at once;
 * `READY` asks again, since flags can resolve differently for this account
 * than for a visitor.
 */
export function handleConfigDispatch(frame: GatewayDispatch): void {
  if (frame.t === 'CONFIG_UPDATE') applyConfig(frame.d as ClientConfig);
  else if (frame.t === 'READY') void loadConfig({ force: true });
}

let inflight: Promise<void> | null = null;

/** `GET /api/v1/config` (signed in or not). A failure keeps the last config. */
export function loadConfig(o: { force?: boolean } = {}): Promise<void> {
  if (inflight && !o.force) return inflight;
  const p = api.client
    .get<ClientConfig>(CLIENT_CONFIG_PATH)
    .then((c) => {
      if (c && typeof c === 'object' && 'flags' in c) applyConfig(c, o);
    })
    .catch(() => {
      if (!useConfig.getState().config) useConfig.setState({ status: 'error' });
    })
    .finally(() => {
      if (inflight === p) inflight = null;
    });
  inflight = p;
  return p;
}

/** False only when the server said this feature is off for you. */
export function flagOn(
  key: FeatureFlagKey,
  c: ClientConfig | null = useConfig.getState().config,
): boolean {
  return c?.flags[key] !== false;
}

export function useFlag(key: FeatureFlagKey): boolean {
  return useConfig((s) => flagOn(key, s.config));
}

/** The console's `display.role_icons` in `c`: `highest` unless the server said `all`. */
export function roleIconMode(c: ClientConfig | null = useConfig.getState().config): RoleIconMode {
  return c?.settings['display.role_icons'] === 'all' ? 'all' : 'highest';
}

/**
 * How many role icons people show (the console's `display.role_icons`):
 * one, their highest role's (until the server says otherwise), or every
 * role's. It follows `CONFIG_UPDATE`, so a switch in the console redraws
 * every open app at once.
 */
export function useRoleIconMode(): RoleIconMode {
  return useConfig((s) => roleIconMode(s.config));
}

/**
 * Banners to show now: active, and not dismissed here (dismissible ones
 * only). One the server says had started when it made the config
 * (`generated_at`) shows at once: the banner's clock only ticks every 30
 * seconds (and may differ from the server's), and an announcement published
 * just now must appear live, not up to 30 seconds later.
 */
export function visibleAnnouncements(
  s: Pick<ConfigState, 'config' | 'dismissed'>,
  nowMs: number,
): PublicAnnouncement[] {
  const generated = s.config ? Date.parse(s.config.generated_at) : NaN;
  return (s.config?.announcements ?? []).filter((a) => {
    const starts = Date.parse(a.starts_at);
    return (
      (starts <= generated || nowMs >= starts) &&
      (!a.ends_at || nowMs < Date.parse(a.ends_at)) &&
      !(a.dismissible && s.dismissed.includes(a.id))
    );
  });
}

export function dismissAnnouncement(id: string): void {
  // keep the list short: only ids the server still sends
  const live = new Set((useConfig.getState().config?.announcements ?? []).map((a) => a.id));
  const dismissed = [...useConfig.getState().dismissed.filter((x) => live.has(x)), id];
  storage.setJson(DISMISSED_KEY, dismissed);
  useConfig.setState({ dismissed });
}

/** The phase now, from the server's state and this clock (a warning turns into maintenance on time). */
export function currentMaintenance(c: ClientConfig | null, nowMs: number): MaintenancePhase {
  return c ? maintenancePhase(c.maintenance, nowMs) : 'off';
}

/** Re-renders at least every `ms` so phases and banner windows change on time. */
export function useMaintenancePhase(ms = 15_000): MaintenancePhase {
  const config = useConfig((s) => s.config);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return currentMaintenance(config, now);
}
